<script>
  import { onMount, tick } from 'svelte';
  import { TS } from '../config/translation-keys.js';
  import { t9n } from '../lib/translate.js';
  import session from '../lib/session';
  import { show_message } from '../lib/flash';
  import APIClient from '../state-charts/api-client';
  import InputWithValidation from '../lib/components/InputField.svelte';
  import Switch from '../lib/components/Switch.svelte';
  import AddTeamDialog from './AddTeamDialog.svelte';
  import TeamDetailsDialog from './TeamDetailsDialog.svelte';

  // component options  ------------------------------------------------------ //

  export let route_name;

  // translated strings ------------------------------------------------------ //

  let change_text = t9n(TS.WORKER_TEAMS_CHANGE_TEXT);
  let private_teams_title = t9n(TS.NEW_ORDER_SUM_PRIVTEAM);
  let own_teams_title = t9n(TS.WORKER_TEAMS_MY_OWN);
  let team_name_title = t9n(TS.ORDERER_TEAMS_TEAM_NAME_TITLE);
  let team_code_title = t9n(TS.ORDERER_TEAMS_TEAM_CODE_TITLE);
  let add_team_title = t9n(TS.ORDERER_TEAMS_ADD_TEAM_TITLE);
  let search_placeholder = t9n(TS.ORDERER_TEAMS_SEARCH_PLACEHOLDER);
  let desc = t9n(TS.WORKER_TEAMS_EXPLANATION_NEW);
  let no_result_text = t9n(TS.TABLE_NO_RESULT);

  // internal variables ------------------------------------------------------ //

  let user;
  let membership_teams = [];
  let public_teams = [];
  let private_teams = [];
  let owned_all_teams = [];
  let own_teams = [];
  let query = '';
  let is_api_running = false;

  // utility functions -------------------------------------------------------- //

  const get_membership_teams = () => {
    membership_teams = $session.user.teams || [];
    filter_membership_teams();
  };

  const filter_membership_teams = () => {
    let public_membership = membership_teams.filter(t => !t.team.is_private);
    let private_membership = membership_teams.filter(t => !!t.team.is_private);

    if (!!query) {
      const searchterm = query.toLowerCase();
      public_membership = public_membership.filter(t =>
        t.team.name.toLowerCase().includes(searchterm)
      );
      private_membership = private_membership.filter(t =>
        t.team.name.toLowerCase().includes(searchterm)
      );
    }

    public_teams = public_membership;
    private_teams = private_membership;
  };

  const filter_own_teams = () => {
    if (!!query) {
      const searchterm = query.toLowerCase();
      own_teams = owned_all_teams.filter(t =>
        t.name.toLowerCase().includes(searchterm)
      );
    } else {
      own_teams = owned_all_teams;
    }
  };

  const filter_teams = () => {
    filter_membership_teams();
    filter_own_teams();
  };

  const get_own_teams = async () => {
    if (is_api_running) return;
    is_api_running = true;

    let data;
    let retryCount = 0;
    const maxRetries = 5;
    const delayBetweenRetries = 1000;

    while (!data && retryCount < maxRetries) {
      try {
        data = await APIClient.get_orderer_teams();
        if (!data) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenRetries));
          retryCount++;
        }
      } catch (error) {
        console.error('Error occurred while fetching data:', error);
        break;
      }
    }

    if (data) {
      owned_all_teams = data.teams || [];
      filter_own_teams();
    }

    is_api_running = false;
  };

  const open_add_team_dialog = e => {
    let target = document.querySelector('#worker-add-team');
    target.innerHTML = '';
    return new AddTeamDialog({
      target: target,
      props: { user: user },
    });
  };

  const open_edit_team_dialog = team_id => {
    let team = owned_all_teams.find(t => t.team_id === team_id);
    let target = document.querySelector('#worker-edit-team');
    target.innerHTML = '';
    return new TeamDetailsDialog({
      target: target,
      props: { team: team },
    });
  };

  const persist_membership = async () => {
    // Wait for Switch bind:value to flush before reading activated flags
    await tick();

    const current_teams = $session.user.teams || [];
    const teams_payload = current_teams.map(t => ({
      activated: t.activated,
      team_id: t.team.team_id,
    }));

    const user = await APIClient.update_profile({ teams: teams_payload });
    if (!user) return;

    // API response can omit/stale teams.activated — keep what we just saved
    const synced_teams = (user.teams || current_teams).map(t => {
      const team_id = t.team?.team_id;
      const saved = teams_payload.find(p => p.team_id === team_id);
      return saved ? { ...t, activated: saved.activated } : t;
    });

    session.write({
      user: {
        ...user,
        teams: synced_teams,
      },
    });

    get_membership_teams();
    show_message(t9n(TS.SETTINGS_LABEL_SAVED), 'success');
  };

  const init = async () => {
    document.querySelector('.mdc-top-app-bar--fixed-adjust').scrollIntoView({behavior: "smooth"})
    user = $session.user;
    get_membership_teams();
    await get_own_teams();

    window.addEventListener('ordererteamdeleted', async () => {
      await get_own_teams();
    });
    window.addEventListener('ordererteamadded', async () => {
      await get_own_teams();
    });
  };

  onMount(async () => {
    await init();
  });

  // reactive functions ------------------------------------------------------- //
</script>

  <main class="main--global wrapper fade-in">

    <!-- Search box -->
    <div style="display: flex; justify-content: space-between">
      <InputWithValidation
        leading_icon="search"
        id="worker-search-field"
        data_cy="search-field"
        type="text"
        margin_bottom={'0px'}
        label={search_placeholder}
        name="search-field"
        bind:value={query}
        on:keyup={filter_teams} />
      <button
        on:click={open_add_team_dialog}
        style="margin-left: 10px; align-self: center"
        class="mdc-button mdc-button--oval mdc-button--raised">
        <span class="mdc-button__ripple" />
        {add_team_title}
      </button>
    </div>

    <!-- Help line -->
    <div
      id="help-liner"
      class="fade-in-long"
      style="margin-top: 10px; margin-bottom: 10px">
      <div class="tooltip-default">
        <div
          id="tooltip_def"
          class="tooltip-text tooltip-top"
          style="background-color:var(--mdc-theme-secondary); color: rgb(255,
          255, 255);">
          {desc}
        </div>
      </div>
    </div>

    <!-- Public teams (from profile user.teams, is_private false) -->
    <h4 class="mdc-typography--headline7 section-title">{change_text}</h4>
    <div class="mdc-data-table table-min-height" style="margin-bottom: 20px">
      <table id="public_teams_table" class="mdc-data-table__table table fitscreen">
        <thead>
          <tr
            class="mdc-data-table__header-row mdc-data-table-bold fade-in"
            style="height: 46px">
            <th class="mdc-data-table__header-cell fade-in">
              {team_name_title}
            </th>
            <th class="mdc-data-table__header-cell fade-in toggle-cell" />
          </tr>
        </thead>
        <tbody class="mdc-data-table__content">
          {#each public_teams as t}
            <tr class="mdc-data-table__row toggle-row">
              <td>{t.team.name}</td>
              <td
                class="toggle-cell"
                on:click|stopPropagation={persist_membership}>
                <Switch
                  id={`worker-public-team-${t.team.team_id}`}
                  label=""
                  bind:value={t.activated} />
              </td>
            </tr>
          {:else}
            <tr class="mdc-data-table__row no-result-row" style="height: 46px">
              <td colspan="2">{no_result_text}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <!-- Private teams (from profile user.teams, is_private true) -->
    <h4 class="mdc-typography--headline7 section-title">{private_teams_title}</h4>
    <div class="mdc-data-table table-min-height" style="margin-bottom: 20px">
      <table id="private_teams_table" class="mdc-data-table__table table fitscreen">
        <thead>
          <tr
            class="mdc-data-table__header-row mdc-data-table-bold fade-in"
            style="height: 46px">
            <th class="mdc-data-table__header-cell fade-in">
              {team_name_title}
            </th>
            <th class="mdc-data-table__header-cell fade-in toggle-cell" />
          </tr>
        </thead>
        <tbody class="mdc-data-table__content">
          {#each private_teams as t}
            <tr class="mdc-data-table__row toggle-row">
              <td>{t.team.name}</td>
              <td
                class="toggle-cell"
                on:click|stopPropagation={persist_membership}>
                <Switch
                  id={`worker-private-team-${t.team.team_id}`}
                  label=""
                  bind:value={t.activated} />
              </td>
            </tr>
          {:else}
            <tr class="mdc-data-table__row no-result-row" style="height: 46px">
              <td colspan="2">{no_result_text}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <!-- My own teams (from GET /teams, no is_private filter) -->
    <h4 class="mdc-typography--headline7 section-title">{own_teams_title}</h4>
    <div class="mdc-data-table table-min-height" style="margin-bottom: 20px">
      <table id="own_teams_table" class="mdc-data-table__table table fitscreen">
        <thead>
          <tr
            class="mdc-data-table__header-row mdc-data-table-bold fade-in"
            style="height: 46px">
            <th class="mdc-data-table__header-cell fade-in">
              {team_name_title}
            </th>
            <th class="mdc-data-table__header-cell fade-in">
              {team_code_title}
            </th>
          </tr>
        </thead>
        <tbody class="mdc-data-table__content">
          {#each own_teams as team}
            <tr
              class="mdc-data-table__row"
              style="height: 46px"
              on:click={() => open_edit_team_dialog(team.team_id)}>
              <td>{team.name}</td>
              <td>
                {#if !!team.code}{team.code}{/if}
              </td>
            </tr>
          {:else}
            <tr class="mdc-data-table__row no-result-row" style="height: 46px">
              <td colspan="2">{no_result_text}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <!-- Hidden dialog add team -->
    <div id="worker-add-team" />

    <!-- Hidden dialog edit team -->
    <div id="worker-edit-team" />

  </main>

<style type="text/scss">
  /* Reset Styles
    *******************************************************/
  * {
    box-sizing: border-box;
    margin: 0;
    text-decoration: none;
  }

  html,
  body,
  main {
    display: flex;
    flex-direction: column;
  }

  /* Actual page
    *******************************************************/
  .row {
    display: flex;
  }

  .column {
    flex: 50%;
  }

  .section-title {
    margin-top: 10px;
    margin-bottom: 8px;
  }

  .mdc-data-table {
    overflow-x: hidden;
    overflow-y: visible;
  }

  .mdc-data-table__table {
    overflow: visible;
  }

  #public_teams_table .mdc-data-table__row,
  #private_teams_table .mdc-data-table__row {
    overflow: visible;
  }

  #own_teams_table .mdc-data-table__row {
    overflow: hidden;
  }

  .toggle-row {
    height: 56px;
  }

  .table thead > tr > th,
  .table tbody > tr > th,
  .table tfoot > tr > th,
  .table thead > tr > td,
  .table tbody > tr > td,
  .table tfoot > tr > td {
    padding: 10px;
  }

  .mdc-data-table__content tr {
    cursor: pointer;
  }

  .mdc-button--oval {
    border-radius: 40px;
    background-color: var(--mdc-theme-primary);
    color: white;
    height: 48px;
    padding-left: 15px;
    padding-right: 15px;
  }

  .mdc-data-table-bold {
    background-color: blanchedalmond;
  }

  .mdc-data-table__header-cell {
    height: 46px;
  }

  .toggle-cell {
    width: 96px;
    min-width: 96px;
    padding: 12px 16px !important;
    text-align: right;
    vertical-align: middle;
    overflow: visible;
  }

  .toggle-cell :global(label) {
    display: none;
  }

  .toggle-cell :global(.mdc-switch) {
    display: inline-flex;
    margin-right: 0;
    vertical-align: middle;
    overflow: visible;
  }

  /* Tooltip
    *******************************************************/

  .tooltip-default {
    position: relative;
    font-size: 12px;
    animation: transition_in 0.6s;
  }

  .tooltip-default .tooltip-top {
    z-index: 1;
    bottom: 100%;
    right: 0;
    left: 50%;
  }

  .tooltip-default .tooltip-text {
    background-color: var(--mdc-theme-secondary);
    color: #fff;
    text-align: center;
    border-radius: 6px;
    padding: 8px 8px;
    display: block;
    padding-left: 30px;
    padding-right: 30px;
    font-size: 13px;
    box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.3);
  }

  .tooltip-default .tooltip-text::before {
    content: ' ';
    position: relative;
    top: -30px;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: transparent transparent #018786 transparent;
    left: 69px;
  }

  .fade-in-long {
    animation: transition_in 2.5s;
  }

  /* Desktop Mode
    *******************************************************/
  @media (min-width: 1025px) {
    .wrapper {
      width: 900px;
      margin: 0 auto;
      margin-bottom: 50px;
      height: fit-content;
      padding: 30px;
    }
  }

  /* Tablet Mode
    *******************************************************/
  @media (min-width: 769px) and (max-width: 1024px) {
    .wrapper {
      width: 90%;
      margin: 0 auto;
      margin-bottom: 50px;
      height: fit-content;
    }
  }

  /* Mobile Mode
    *******************************************************/
  @media (min-width: 0) and (max-width: 768px) {
    .wrapper {
      /*width: 100%;*/
      margin: 0 auto;
      margin-left: 50px;
      margin-bottom: 50px;
      height: fit-content;
    }

    .row {
      display: block;
    }
  }
</style>
